// Copyright (c) Ping. All rights reserved.
//
// localStorage-backed web persisters for personal message bookmarks:
//   • SAVED (§2.7 0x62/0x63) — per-conversation, keyed `com.ping.saved.<conv>`,
//     the SAME key `WebChatRoom` reads on mount, so a sibling-device save applied
//     in the background by `useIncomingMessages` surfaces the moment the room
//     opens (closing the "only converges while the room is mounted" gap).
//   • STARRED (§2.7 0x64) — a flat DeviceGroup-synced set of content hashes.
// Both dispatch a `storage`-style event via a tiny subscriber registry so a
// mounted view re-reads live.

import type { SavedPersister, StarredPersister } from '@ping/ui/src/sync/persistenceAdapters';

const SAVED_KEY = (conversationId: string): string => `com.ping.saved.${conversationId}`;
const STARRED_KEY = 'com.ping.starred.v1';
// Same key `WebChatRoom` reads/writes for pins, so a pin toggled from a thread
// panel converges the moment the room re-reads (on open / conversation change).
const PINNED_KEY = (conversationId: string): string => `com.ping.pins.${conversationId}`;

function readSet(key: string): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSet(key: string, set: Set<string>): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify([...set]));
  } catch {
    /* best-effort */
  }
}

// Per-key subscriber registry (localStorage events don't fire in the same tab).
const subscribers = new Map<string, Set<(targets: readonly string[]) => void>>();
function notify(key: string): void {
  const subs = subscribers.get(key);
  if (subs === undefined) return;
  const targets = [...readSet(key)];
  for (const cb of subs) cb(targets);
}
function subscribe(key: string, cb: (targets: readonly string[]) => void): () => void {
  let subs = subscribers.get(key);
  if (subs === undefined) {
    subs = new Set();
    subscribers.set(key, subs);
  }
  subs.add(cb);
  cb([...readSet(key)]); // prime
  return () => {
    subs?.delete(cb);
    // Reclaim the per-key Set once its last subscriber leaves, so visiting many
    // conversations over a long session doesn't retain an empty Set per key.
    if (subs !== undefined && subs.size === 0) subscribers.delete(key);
  };
}

export const webSavedPersister: SavedPersister = {
  async set(conversationId, target, saved) {
    const key = SAVED_KEY(conversationId);
    const set = readSet(key);
    if (saved) set.add(target);
    else set.delete(target);
    writeSet(key, set);
    notify(key);
  },
  async listByConversation(conversationId) {
    return [...readSet(SAVED_KEY(conversationId))];
  },
  subscribe(conversationId, cb) {
    return subscribe(SAVED_KEY(conversationId), cb);
  },
};

// Pinned messages — per-conversation (same shape as SavedPersister). Shares
// the `com.ping.pins.<conv>` localStorage key with WebChatRoom so the thread
// panels and the main room agree on which messages are pinned.
export const webPinnedPersister: SavedPersister = {
  async set(conversationId, target, pinned) {
    const key = PINNED_KEY(conversationId);
    const set = readSet(key);
    if (pinned) set.add(target);
    else set.delete(target);
    writeSet(key, set);
    notify(key);
  },
  async listByConversation(conversationId) {
    return [...readSet(PINNED_KEY(conversationId))];
  },
  subscribe(conversationId, cb) {
    return subscribe(PINNED_KEY(conversationId), cb);
  },
};

export const webStarredPersister: StarredPersister = {
  async set(target, starred) {
    const set = readSet(STARRED_KEY);
    if (starred) set.add(target);
    else set.delete(target);
    writeSet(STARRED_KEY, set);
    notify(STARRED_KEY);
  },
  async list() {
    return [...readSet(STARRED_KEY)];
  },
  subscribe(cb) {
    return subscribe(STARRED_KEY, cb);
  },
};
